import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Customer, CustomerPickerSummary } from './customers.model';
import { environment } from '../../../environments/environment';
import { Page } from '../../models/page-wrapper.model';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';

@Injectable({
  providedIn: 'root'
})
export class CustomersService {
  private apiUrl = `${environment.restApiUrl}masterdata/customers`;
  private readonly pickerEntityKey = 'customer';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) { }

  private handleError(error: HttpErrorResponse) {
    // A client-side or network error occurred.
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${JSON.stringify(error.error)}`
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  // getAllCustomers(params?: any): Observable<Page<Customer>> {
  //   return this.http.get<Page<Customer>>(this.apiUrl, { params }).pipe(catchError(this.handleError));
  // }

  getAllCustomers(params?: any): Observable<Page<Customer>> {
    return this.http.get<Page<Customer>>(this.apiUrl+'/search', { params:params }).pipe(catchError(this.handleError));

  }

  searchCustomers(query: string, page = 0, size = 25): Observable<Page<Customer>> {
    return forkJoin([
      this.searchCustomersByField('customerName', query, page, size),
      this.searchCustomersByField('customerNumber', query, page, size),
    ]).pipe(
      map(([namePage, numberPage]) => this.mergeCustomerPages(namePage, numberPage, page, size)),
      catchError(this.handleError)
    );
  }

  getCustomerById(id: number, loadLocations = false): Observable<Customer> {
    const params = loadLocations ? { loadLocations: true } : undefined;
    return this.http.get<Customer>(`${this.apiUrl}/${id}`, { params }).pipe(catchError(this.handleError));
  }

  loadRecentCustomers(limit = 10): Observable<CustomerPickerSummary[]> {
    return from(this.recentPickerCache.loadRecent<CustomerPickerSummary>(this.pickerEntityKey, limit));
  }

  getFreshCustomerById(id: number | string): Observable<Customer> {
    return this.getCustomerById(Number(id), true);
  }

  rememberCustomer(customer: Customer | CustomerPickerSummary): Observable<void> {
    const summary: CustomerPickerSummary = {
      id: customer.id,
      customerNumber: customer.customerNumber,
      customerName: customer.customerName,
    };

    const record: PickerRecentRecord<CustomerPickerSummary> = {
      key: `${this.pickerEntityKey}::${summary.id}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: summary.id,
      label: `${summary.customerNumber} - ${summary.customerName}`,
      payload: summary,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createCustomerPickerAdapter(): EntityDropdownPickerAdapter<CustomerPickerSummary, Customer> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentCustomers(limit),
      search: (query: string, page: number, size: number) =>
        this.searchCustomers(query, page, size).pipe(
          map((response): Page<CustomerPickerSummary> => ({
            ...response,
            content: (response.content || []).map((customer) => ({
              id: customer.id,
              customerNumber: customer.customerNumber,
              customerName: customer.customerName,
            })),
          }))
        ),
      getFreshById: (id: number | string) => this.getFreshCustomerById(id),
      rememberSelection: (customer: Customer) => this.rememberCustomer(customer),
      getId: (customer: CustomerPickerSummary | Customer) => customer.id,
      getLabel: (customer: CustomerPickerSummary | Customer) => `${customer.customerNumber} - ${customer.customerName}`,
      getSecondaryLabel: () => '',
      filterBy: 'customerName,customerNumber',
    };
  }

  deleteCustomer(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }

  private searchCustomersByField(
    field: 'customerName' | 'customerNumber',
    query: string,
    page: number,
    size: number
  ): Observable<Page<Customer>> {
    const params = {
      [field]: query,
      page,
      size,
      loadLocations: false,
    };

    return this.http.get<Page<Customer>>(`${this.apiUrl}/search`, { params }).pipe(
      catchError(() => of(this.emptyCustomerPage(page, size)))
    );
  }

  private mergeCustomerPages(
    namePage: Page<Customer>,
    numberPage: Page<Customer>,
    page: number,
    size: number
  ): Page<Customer> {
    const merged = new Map<number, Customer>();
    [...(namePage.content || []), ...(numberPage.content || [])].forEach((customer) => {
      if (customer?.id !== undefined && customer?.id !== null) {
        merged.set(customer.id, customer);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);

    return {
      ...namePage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptyCustomerPage(page: number, size: number): Page<Customer> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }
}
